import {
  Brand,
  Cancellable,
  CmdBaseParams,
  FavoriteListIdentifier,
  IKmmFlowRepository,
  KMM,
  KmmApi,
  KtMap,
  Level,
  LoggerOptions,
  NtvbBaseParams,
  Options,
  PersistenceSettings,
  NorlysTvBackendApi,
  NorlysTvBackendRepository,
  createIKmmFlowRepository,
  KmmRepository,
  KMMError
} from '@norlys-a-s/norlys-play-kmm';
import { getDefaultStore } from 'jotai';
import { errorAtom, isErrorDialogOpenAtom, isStreamingAtom, playerFocusKeyAtom } from '../../atoms';
import { networkError, BaseError, CmdError } from '@smart-tv/contexts/error-context/error-context';
import { checkNetwork } from './check-network';
import { isOfflineAtom } from '@smart-tv/hooks/use-network/use-network';
import { getUid } from '../api-utils';
import { ExtendedOAuthTokenStorage } from './ExtendedOAuthTokenStorage';
import { getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import {
  getRepoMethodName,
  startNetworkLogEntry,
  finishNetworkLogEntry,
  createCallRecordingProxy,
  argsToParams,
  CapturedCall
} from '../network-log';
import {
  CMD_URL,
  CMD_BASE_URL,
  APP_VERSION,
  MANUFACTURER,
  MODEL,
  NTVB_BASE_URL,
  NTVB_AUTH_BASE_URL,
  NTVB_CLIENT_ID,
  NTVB_CLIENT_SECRET,
  KMM_LOG_LEVEL
} from '../api-constants';

export const tokenStorage: ExtendedOAuthTokenStorage = ExtendedOAuthTokenStorage.create();

function createApiFacade() {
  const baseParamsProvider = () => {
    const uid = getUid();
    return new CmdBaseParams(
      CMD_URL,
      APP_VERSION,
      uid,
      MANUFACTURER,
      MODEL,
      uid,
      null,
      Brand.Norlys,
      null,
      null,
      null
    );
  };

  const ntvbBaseParamsProvider = () =>
    new NtvbBaseParams(
      NTVB_BASE_URL,
      NTVB_AUTH_BASE_URL,
      getUid(),
      NTVB_CLIENT_ID,
      NTVB_CLIENT_SECRET,
      MANUFACTURER,
      MODEL,
      APP_VERSION,
      null,
      null
    );

  return new KmmApi(
    new Options(new LoggerOptions(Level.valueOf(KMM_LOG_LEVEL)), undefined),
    PersistenceSettings.create(),
    () => true,
    tokenStorage,
    ntvbBaseParamsProvider,
    baseParamsProvider
  );
}

KMM.getInstance().initialize();

const apiFacade = createApiFacade();
const repositoryFacade = new KmmRepository(apiFacade);
const flowRepositoryFacadePromise: Promise<IKmmFlowRepository> =
  createIKmmFlowRepository(repositoryFacade);

const ntvbApi = new NorlysTvBackendApi(
  () => {
    const uid = getUid();
    return new NtvbBaseParams(
      NTVB_BASE_URL,
      NTVB_AUTH_BASE_URL,
      uid,
      NTVB_CLIENT_ID,
      NTVB_CLIENT_SECRET,
      MANUFACTURER,
      MODEL,
      APP_VERSION,
      null,
      null
    );
  },
  () => {
    const uid = getUid();
    return new CmdBaseParams(
      CMD_BASE_URL,
      APP_VERSION,
      uid,
      MANUFACTURER,
      MODEL,
      uid,
      null,
      Brand.Norlys,
      null,
      null,
      null
    );
  },
  undefined,
  tokenStorage
);

export const ntvbRepo = new NorlysTvBackendRepository(ntvbApi);

/**
 * Helper to call methods that only exist on the raw NorlysTvBackendApi (not exposed on
 * NorlysTvBackendRepository), e.g. getActiveFeatureIntros. Mirrors ntvb()'s error handling.
 */
export async function ntvbApiCall<T>(
  call: (api: NorlysTvBackendApi) => Promise<T>,
  handleErrorGenerally = true
): Promise<T> {
  const start = performance.now();
  const captured: CapturedCall = {};
  const proxiedApi = createCallRecordingProxy(ntvbApi, captured);
  let logId: number | undefined;
  try {
    const promise = call(proxiedApi);
    logId = startNetworkLogEntry(
      captured.method ?? getRepoMethodName(call),
      'NTVB',
      argsToParams(captured.args)
    );
    const result = await promise;
    finishNetworkLogEntry(logId, {
      status: 'success',
      duration: Math.round(performance.now() - start),
      response: result
    });
    return result;
  } catch (error) {
    logId =
      logId ??
      startNetworkLogEntry(captured.method ?? getRepoMethodName(call), 'NTVB', argsToParams(captured.args));
    finishNetworkLogEntry(logId, {
      status: 'error',
      duration: Math.round(performance.now() - start),
      error
    });
    if (!(await checkNetwork())) throw networkError;
    if (error instanceof KMMError && handleErrorGenerally) handleKmmError(error);
    throw error;
  }
}

/**
 * Helper functions  to call kmm ntvb repository and flow repository functions.
 * Handles network detection: throws networkError if offline, re-throws the original error otherwise.
 */
export async function ntvb<T>(
  call: (repo: NorlysTvBackendRepository) => Promise<T>,
  handleErrorGenerally = true
): Promise<T | undefined> {
  const start = performance.now();
  const captured: CapturedCall = {};
  const proxiedRepo = createCallRecordingProxy(ntvbRepo, captured);
  let logId: number | undefined;
  try {
    const promise = call(proxiedRepo);
    logId = startNetworkLogEntry(
      captured.method ?? getRepoMethodName(call),
      'NTVB',
      argsToParams(captured.args)
    );
    const result = await promise;
    finishNetworkLogEntry(logId, {
      status: 'success',
      duration: Math.round(performance.now() - start),
      response: result
    });
    return result;
  } catch (error) {
    logId = logId ?? startNetworkLogEntry(captured.method ?? getRepoMethodName(call), 'NTVB', argsToParams(captured.args));
    finishNetworkLogEntry(logId, {
      status: 'error',
      duration: Math.round(performance.now() - start),
      error
    });
    if (!(await checkNetwork())) throw networkError;
    //only handle the error here if generel case
    if (error instanceof KMMError && handleErrorGenerally) handleKmmError(error);
    throw error;
  }
}

export async function kmm<T>(
  call: (repo: KmmRepository) => Promise<T>,
  handleErrorGenerally = true
): Promise<T> {
  const start = performance.now();
  const captured: CapturedCall = {};
  const proxiedRepo = createCallRecordingProxy(repositoryFacade, captured);
  let logId: number | undefined;
  try {
    const promise = call(proxiedRepo);
    logId = startNetworkLogEntry(
      captured.method ?? getRepoMethodName(call),
      'KMM',
      argsToParams(captured.args)
    );
    const result = await promise;
    finishNetworkLogEntry(logId, {
      status: 'success',
      duration: Math.round(performance.now() - start),
      response: result
    });
    return result;
  } catch (error) {
    logId = logId ?? startNetworkLogEntry(captured.method ?? getRepoMethodName(call), 'KMM', argsToParams(captured.args));
    finishNetworkLogEntry(logId, {
      status: 'error',
      duration: Math.round(performance.now() - start),
      error
    });
    if (!(await checkNetwork())) throw networkError;
    //only handle the error here if generel case
    if (error instanceof KMMError && handleErrorGenerally) handleKmmError(error);
    throw error;
  }
}

export async function kmmRequest(
  cmd: string,
  parameters: Record<string, unknown>,
  handleErrorGenerally = true,
  authenticated = true
): Promise<string> {
  const start = performance.now();
  const logId = startNetworkLogEntry(cmd, 'KMM_REQUEST', parameters);
  try {
    const result = await apiFacade.request(
      cmd,
      KtMap.fromJsMap(new Map(Object.entries(parameters))),
      authenticated
    );
    finishNetworkLogEntry(logId, {
      status: 'success',
      duration: Math.round(performance.now() - start),
      response: result
    });
    return result;
  } catch (error) {
    finishNetworkLogEntry(logId, {
      status: 'error',
      duration: Math.round(performance.now() - start),
      error
    });
    if (!(await checkNetwork())) throw networkError;
    if (error instanceof KMMError && handleErrorGenerally) handleKmmError(error);
    throw error;
  }
}

export function getFavoriteListIdentifierForKmm(id: number): FavoriteListIdentifier {
  if (id === -2) {
    return FavoriteListIdentifier.AllUserChannels;
  }

  return new FavoriteListIdentifier.ActiveFavoriteList(id);
}

export interface FlowSubscription {
  cancel: () => void;
}

export function flowSubscribe(start: (repo: IKmmFlowRepository) => Cancellable): FlowSubscription {
  let inner: Cancellable | undefined;
  let cancelled = false;

  void flowRepositoryFacadePromise.then((flowRepo) => {
    if (cancelled) return;
    inner = start(flowRepo);
    if (cancelled) inner.cancel();
  });

  return {
    cancel: () => {
      cancelled = true;
      inner?.cancel();
      inner = undefined;
    }
  };
}

export function handleKmmError(err?: KMMError): boolean {
  //default store uses globalThis in the jotai package, which is not available on tizen 5.5 and below. We have added a polyfill for globalThis in the polyfills.js file, which is included in index.html. This allows us to use the default store on tizen 5.5 and below.
  const store = getDefaultStore();
  const errorOpen = store.get(isErrorDialogOpenAtom);
  const isPlaying = store.get(isStreamingAtom);
  const playerFocusKey = store.get(playerFocusKeyAtom);
  if (errorOpen) return false; // Prevent overwriting error dialog with a new one if it's already open
  if (!err) {
    store.set(errorAtom, null);
    store.set(isErrorDialogOpenAtom, false);
    return false;
  }
  if (store.get(isOfflineAtom)) return false;
  store.set(errorAtom, {
    err,
    prevFocusKey: getCurrentFocusKey(),
    isStreamPlaying: isPlaying,
    playerFocusKey: playerFocusKey
  });
  return true;
}
